import { Immutable } from "immer";
import * as t from "io-ts";
import React from "react";
import {
    RetailState as BaseRetailState,
    StoreSearchFilters,
} from "tsi-common-react/src/apps/retail/models";
import {
    Latitude,
    Longitude,
    ProductUUID,
    SyncStoreID,
    WebPageURL,
} from "tsi-common-react/src/models/nominals";
import { nullable } from "tsi-common-react/src/models/utils";

const HolidayHourPeriod = t.readonly(
    t.intersection([
        t.interface({
            date: t.string,
            closed: t.boolean,
        }),
        t.partial({
            start_time: t.string,
            end_time: t.string,
        }),
    ]),
);

const HolidayHoursLine = t.readonly(
    t.interface({
        label: t.string,
        schedule: t.string,
    }),
);
export type IHolidayHoursLine = t.TypeOf<typeof HolidayHoursLine>;

export const CMSFlagshipStorePage = t.interface({
    id: t.number,
    meta: t.interface({
        type: t.string,
        detail_url: t.string,
        html_url: WebPageURL,
        slug: t.string,
        first_published_at: t.string,
        sync_id: SyncStoreID,
    }),
    title: t.string,
    address: t.string,
    address2: t.string,
    city: t.string,
    state: t.string,
    postal: t.string,
    storefront: nullable(
        t.interface({
            id: t.number,
            meta: t.interface({
                type: t.string,
                detail_url: t.string,
                download_url: t.string,
            }),
            title: t.string,
        }),
    ),
    storefront_thumbnail: nullable(
        t.interface({
            url: t.string,
            width: t.number,
            height: t.number,
        }),
    ),
    hours_sun: t.string,
    hours_mon: t.string,
    hours_tues: t.string,
    hours_wed: t.string,
    hours_thurs: t.string,
    hours_fri: t.string,
    hours_sat: t.string,
    holiday_hours: t.readonlyArray(HolidayHourPeriod),
    upcoming_holiday_hours_summary: t.readonlyArray(HolidayHoursLine),
    hours_summary: t.string,
    hours_summary_abbr: t.string,
    located_near: t.string,
    shopping_district: t.string,
});

export type CMSFlagshipStorePage = t.TypeOf<typeof CMSFlagshipStorePage>;

const CMSFlagshipStorePages = t.array(CMSFlagshipStorePage);

export const CMSFlagshipStorePagesAPIPage = t.interface({
    meta: t.interface({
        total_count: t.number,
    }),
    items: CMSFlagshipStorePages,
});

export type CMSStoreHourDays =
    | "hours_sun"
    | "hours_mon"
    | "hours_tues"
    | "hours_wed"
    | "hours_thurs"
    | "hours_fri"
    | "hours_sat";

export type FilterChangeHandler = (
    filters: Partial<StoreSearchFilters>,
) => void;

export interface QuickSearchItem {
    id: string;
    title: string | React.ReactNode;
    descr: string | React.ReactNode;
    labelClassName: string;
    imgSrc: string;
    imgAlt: string;
    href: string;
    defaultFilterState: Partial<StoreSearchFilters>;
    selected?: boolean;
}

interface SendStorePostDataBase {
    stores: Array<{
        address1: string;
        address2: string;
        city: string;
        distance: string;
        name: string;
        phone: string;
        state: string;
        url: string;
        zip: string;
        lat?: Latitude;
        lng?: Longitude;
    }>;
}

export interface EmailSendStorePostData extends SendStorePostDataBase {
    email: string;
}

export type EmailLocatorResultsPostData = Immutable<{
    email: string;
    stores: Array<{
        id: SyncStoreID;
        distance: number | null;
    }>;
}>;

export interface TXTSendStorePostData extends SendStorePostDataBase {
    brand: "TP";
    triggerExternalCall: boolean;
    phone: string;
    program: "text";
}

export type SendStorePostData = EmailSendStorePostData | TXTSendStorePostData;

export interface SurveyModalPostData {
    brand: "TP";
    program: "survey";
    email: string;
    surveyModalResult: string;
}

export interface RetailState extends BaseRetailState {
    // Map Product UUIDs to sets of Store External IDs that have that product in stock
    productAvailability: {
        [productUUID: string]: SyncStoreID[];
    };

    // UUID of selected product
    selectedProduct: ProductUUID | null;

    // Map of Flagship Store External ID to it's CMS pages
    flagshipStorePages: {
        [externalID: number]: CMSFlagshipStorePage;
    };
}
